import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { LogOut, Menu } from "lucide-react";
import { B, bText } from "../tokens";

interface BTopBarProps {
  /** Current section, shown on the left (e.g. "Workspace / Receipts"). */
  title?: ReactNode;
  /** Two-letter initials shown in the user avatar. */
  userInitials: string;
  /** Sign the user out. */
  onLogout: () => void;
  /** Tooltip on the avatar (usually the user's name or email). */
  userTitle?: string;
  /** When set, the avatar becomes a Link to this route. */
  profileTo?: string;
  /** Extra controls placed before the avatar. */
  right?: ReactNode;
  /** Shows a menu button (small screens) that calls this. */
  onMenu?: () => void;
}

// The in-app top bar: section title on the left, account controls on the right.
export default function BTopBar({ title, userInitials, onLogout, userTitle, profileTo, right, onMenu }: BTopBarProps) {
  const avatarStyle = {
    width: 32,
    height: 32,
    borderRadius: 99,
    background: B.accentSoft,
    color: B.accent,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 600,
    fontSize: bText.xs,
    textDecoration: "none",
    boxShadow: `inset 0 0 0 1px ${B.tints.sage.stroke}`,
  } as const;

  return (
    <header
      style={{
        height: 56,
        padding: "0 24px",
        borderBottom: `1px solid ${B.line}`,
        background: "rgba(255,255,255,.85)",
        backdropFilter: "saturate(180%) blur(8px)",
        display: "flex",
        alignItems: "center",
        gap: 12,
        flex: "0 0 auto",
      }}
    >
      {onMenu && (
        <button type="button" className="icon-btn topbar-menu" onClick={onMenu} aria-label="Open navigation">
          <Menu size={18} />
        </button>
      )}
      <div style={{ fontSize: bText.md, color: B.ink2, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {title}
      </div>
      <div style={{ marginLeft: "auto", display: "flex", gap: 8, alignItems: "center" }}>
        {right}
        {profileTo ? (
          <Link to={profileTo} style={avatarStyle} title={userTitle}>
            {userInitials || "?"}
          </Link>
        ) : (
          <span style={avatarStyle} title={userTitle}>
            {userInitials || "?"}
          </span>
        )}
        <button type="button" className="icon-btn" onClick={onLogout} title="Log out" aria-label="Log out">
          <LogOut size={16} />
        </button>
      </div>
    </header>
  );
}
